/**
 * These transform the JSON data from the server before storing it in the Redux store.
 */

import { ROLES } from 'constants/roles';

interface ServerUser {
  id?: string | number;
  firstName?: string;
  lastName?: string;
  authority?: string;
  client?: ServerClient | null;
  officeAddress1?: string;
  officeAddress2?: string;
  officeCity?: string;
  officeState?: string;
  officeZip?: string;
  [key: string]: unknown;
}

interface ServerConsultant {
  id?: string | number;
  consultantType?: string;
  user: ServerUser;
  [key: string]: unknown;
}

interface ServerClient {
  id?: string | number;
  consultants?: ServerConsultant[];
  [key: string]: unknown;
}

type SingularType = 'user' | 'client' | string;

interface UserCapabilities {
  addAndDeleteClients: boolean;
  addAndDeleteUsers: boolean;
  lockAndUnlockClients: boolean;
  lockAndUnlockAllUsers: boolean;
  lockAndUnlockClienUsers: boolean;
  lockAndUnlockClientUsers?: boolean;
  editClient: boolean;
  editClientUser: boolean;
  editFdUserRole: boolean;
  sendPasswordReset: boolean;
  reportsInteractions?: boolean;
  viewPortalAsUser: boolean;
}

const transformers: Record<string, (record: unknown) => unknown> = {
  user: (record) => transformUser(record as ServerUser),
  client: (record) => transformClient(record as ServerClient),
};

export const transformRecordFromServer = (
  singularType: SingularType,
  record: unknown,
): unknown => {
  if (transformers[singularType]) return transformers[singularType](record);
  else return record;
};

function transformUser(user: ServerUser): Record<string, unknown> {
  const client: Record<string, unknown> | null | undefined =
    user.client && transformClient(user.client);

  return {
    ...user,
    name: `${user.firstName} ${user.lastName}`,
    superAdmin: user.authority === ROLES.SUPER_ADMIN,
    fdPrincipal: user.authority === ROLES.FD_PRINCIPAL,
    fdClientService: user.authority === ROLES.FD_CLIENT_SERVICE,
    fdAnalyst: user.authority === ROLES.FD_ANALYST,
    internalUser: user.authority === ROLES.INTERNAL_USER,
    reportAdmin: user.authority === ROLES.REPORT_ADMIN,
    admin:
      user.authority === ROLES.SUPER_ADMIN ||
      user.authority === ROLES.FD_PRINCIPAL ||
      user.authority === ROLES.FD_CLIENT_SERVICE ||
      user.authority === ROLES.REPORT_ADMIN,
    internalAdmin:
      user.authority === ROLES.FD_PRINCIPAL ||
      user.authority === ROLES.FD_CLIENT_SERVICE ||
      user.authority === ROLES.FD_ANALYST ||
      user.authority === ROLES.REPORT_ADMIN,
    fdUser:
      user.authority === ROLES.SUPER_ADMIN ||
      user.authority === ROLES.FD_PRINCIPAL ||
      user.authority === ROLES.FD_CLIENT_SERVICE ||
      user.authority === ROLES.FD_ANALYST ||
      user.authority === ROLES.REPORT_ADMIN,
    basicUser: !user.authority === (ROLES.SUPER_ADMIN as unknown as boolean),
    ...getUserCapabilitiesFromUserRole(user.authority),
    officeAddressFull: [
      user.officeAddress1,
      user.officeAddress2,
      user.officeCity,
      `${user.officeState} ${user.officeZip}`,
    ]
      .filter((line) => line && line.trim() !== '')
      .join(', '),

    client: client,
  };
}

const getUserCapabilitiesFromUserRole = (
  userAuthority: string | undefined,
): UserCapabilities => {
  const capabilities: UserCapabilities = {
    addAndDeleteClients: false,
    addAndDeleteUsers: false,
    lockAndUnlockClients: false,
    lockAndUnlockAllUsers: false,
    lockAndUnlockClienUsers: false,
    editClient: false,
    editClientUser: false,
    editFdUserRole: false,
    sendPasswordReset: false,
    viewPortalAsUser: false,
  };

  if (!userAuthority) {
    return capabilities;
  } else {
    return {
      ...capabilities,
      addAndDeleteClients: userAuthority === ROLES.SUPER_ADMIN,

      addAndDeleteUsers: userAuthority === ROLES.SUPER_ADMIN,

      lockAndUnlockClients: userAuthority === ROLES.SUPER_ADMIN,

      lockAndUnlockAllUsers: userAuthority === ROLES.SUPER_ADMIN,

      lockAndUnlockClientUsers:
        userAuthority === ROLES.SUPER_ADMIN ||
        userAuthority === ROLES.FD_PRINCIPAL ||
        userAuthority === ROLES.FD_CLIENT_SERVICE ||
        userAuthority === ROLES.REPORT_ADMIN,

      editClient:
        userAuthority === ROLES.SUPER_ADMIN ||
        userAuthority === ROLES.FD_PRINCIPAL ||
        userAuthority === ROLES.FD_CLIENT_SERVICE ||
        userAuthority === ROLES.REPORT_ADMIN,

      editClientUser:
        userAuthority === ROLES.SUPER_ADMIN ||
        userAuthority === ROLES.FD_PRINCIPAL ||
        userAuthority === ROLES.FD_CLIENT_SERVICE ||
        userAuthority === ROLES.REPORT_ADMIN,

      editFdUserRole:
        userAuthority === ROLES.SUPER_ADMIN ||
        userAuthority === ROLES.FD_PRINCIPAL ||
        userAuthority === ROLES.FD_CLIENT_SERVICE ||
        userAuthority === ROLES.REPORT_ADMIN,

      sendPasswordReset:
        userAuthority === ROLES.SUPER_ADMIN ||
        userAuthority === ROLES.FD_PRINCIPAL ||
        userAuthority === ROLES.FD_CLIENT_SERVICE ||
        userAuthority === ROLES.REPORT_ADMIN,

      reportsInteractions:
        userAuthority === ROLES.REPORT_ADMIN ||
        userAuthority === ROLES.SUPER_ADMIN,

      viewPortalAsUser:
        userAuthority === ROLES.USER || userAuthority === ROLES.INTERNAL_USER,
    };
  }
};

function transformClient(client: ServerClient): Record<string, unknown> {
  let primaryAdmin =
    client.consultants &&
    client.consultants.find((c) => c.consultantType === 'PRIMARY_ADMIN');

  if (primaryAdmin)
    primaryAdmin = {
      ...primaryAdmin,
      user: { ...primaryAdmin.user, client: null },
    };

  return {
    ...client,
    consultants:
      client.consultants &&
      client.consultants.map(
        (consultant): Record<string, unknown> => ({
          ...consultant,
          user: transformUser(consultant.user),
        })
      ),

    _primaryAdmin: primaryAdmin && transformUser(primaryAdmin.user),
  };
}
